import { MouseEvent } from 'react';

import { SearchQueryState } from '../hooks/useSearchQuery';
import { SearchSubmissionTarget } from '../SearchOverlay.types';
import { FallbackRow } from '../utils/fallbackRows';

import { SearchEmptyState } from './SearchResults/SearchEmptyState';
import { SearchErrorState } from './SearchResults/SearchErrorState';
import { SearchLoadingState } from './SearchResults/SearchLoadingState';
import { SearchNoResultsState } from './SearchResults/SearchNoResultsState';
import { SearchResultsState, SearchResultsStateProps } from './SearchResults/SearchResultsState';

export type SearchResultsProps = {
    state: SearchQueryState;
    emptyFallbackRows: FallbackRow[];
    noResultsFallbackRows: FallbackRow[];
    isDestinationWithRegions: boolean;
    activeIndex: number | null;
    pendingIndex: number | null;
    itemRef: (index: number, el: HTMLAnchorElement | null) => void;
    onMouseEnter: (index: number) => void;
    getOptionId: (index: number) => string;
    onResultClick: (
        event: MouseEvent<HTMLAnchorElement>,
        suggestion: SearchResultsStateProps['suggestions'][number],
        index: number,
    ) => void;
    onFallbackSelect: (target: SearchSubmissionTarget, index: number) => void;
};

export function SearchResults({
    state,
    emptyFallbackRows,
    noResultsFallbackRows,
    isDestinationWithRegions,
    activeIndex,
    pendingIndex,
    itemRef,
    onMouseEnter,
    getOptionId,
    onResultClick,
    onFallbackSelect,
}: SearchResultsProps) {
    switch (state.kind) {
        case 'empty':
            if (emptyFallbackRows.length === 0) return null;
            return (
                <SearchEmptyState
                    rows={emptyFallbackRows}
                    activeIndex={activeIndex}
                    pendingIndex={pendingIndex}
                    itemRef={itemRef}
                    onMouseEnter={onMouseEnter}
                    getOptionId={getOptionId}
                    onSelect={onFallbackSelect}
                />
            );
        case 'loading':
            return <SearchLoadingState />;
        case 'error':
            return <SearchErrorState onRetry={state.retry} />;
        case 'no-results':
            return (
                <SearchNoResultsState
                    query={state.query}
                    rows={noResultsFallbackRows}
                    activeIndex={activeIndex}
                    pendingIndex={pendingIndex}
                    itemRef={itemRef}
                    onMouseEnter={onMouseEnter}
                    getOptionId={getOptionId}
                    onSelect={onFallbackSelect}
                />
            );
        case 'results':
            return (
                <SearchResultsState
                    suggestions={state.suggestions}
                    isDestinationWithRegions={isDestinationWithRegions}
                    activeIndex={activeIndex}
                    pendingIndex={pendingIndex}
                    itemRef={itemRef}
                    onMouseEnter={onMouseEnter}
                    getOptionId={getOptionId}
                    onClick={onResultClick}
                />
            );
    }
}
